import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useOptionalTranslation } from '../../i18n';
import { logger } from '../../utils/logger';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// The outermost boundary sits outside I18nProvider, so the fallback can't require it
const DefaultErrorFallback: React.FC<{ error: Error | null }> = ({ error }) => {
  const { t } = useOptionalTranslation();

  return (
    <div className="error-boundary">
      <div className="error-boundary-content">
        <h2>{t('errors.boundaryTitle')}</h2>
        <p>{t('errors.boundaryMessage')}</p>
        <details className="error-details">
          <summary>{t('errors.boundaryDetails')}</summary>
          <pre>{error?.stack}</pre>
        </details>
        <button
          onClick={() => window.location.reload()}
          className="reload-button"
        >
          {t('errors.boundaryReload')}
        </button>
      </div>
    </div>
  );
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error', error, {
      kind: 'boundary',
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <DefaultErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
